import { useState } from "react";

import { Column } from "@/components/Column";
import { Text } from "@/components/Text";
import {
  FIELD_CONTENT_CLASS,
  FIELD_TRIGGER_CLASS,
} from "@/components/tools/sadie-simulator/SadieSimulatorField";
import { Button } from "@ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ui/dialog";
import { Input } from "@ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";
import { Textarea } from "@ui/textarea";

import { sadieSimulator } from "@/lib/sadieSimulator";
import {
  SadieSimulatorPromptChange,
  SadieSimulatorPromptSection,
} from "@/types";

type ChangeKind = "CHANGE" | "ADD" | "REMOVE";

const KIND_LABELS: Record<ChangeKind, string> = {
  CHANGE: "Change a section",
  ADD: "Add a section",
  REMOVE: "Remove a section",
};

// A change to Sadie's prompt, made against the sections her last turn built.
// A section to change starts from its current text. With `initialChange` it
// edits that change, which keeps its kind.
export const SadieSimulatorPromptChangeDialog = ({
  initialChange,
  isSaving,
  onClose,
  onSave,
  sections,
}: {
  initialChange?: SadieSimulatorPromptChange;
  isSaving: boolean;
  onClose: () => void;
  onSave: (change: SadieSimulatorPromptChange) => Promise<void>;
  sections: SadieSimulatorPromptSection[];
}) => {
  /* React Hooks */
  const [kind, setKind] = useState<ChangeKind>(() =>
    initialChange ? sadieSimulator.changeKind(initialChange) : "CHANGE",
  );
  const [heading, setHeading] = useState(() =>
    initialChange ? initialChange.heading : "",
  );
  const [after, setAfter] = useState(
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    () => initialChange?.after || "",
  );
  const [text, setText] = useState(() =>
    initialChange ? initialChange.text : "",
  );

  /* Helpers */
  const isReady = () => {
    if (!heading.trim()) return false;
    if (kind === "REMOVE") return true;
    if (kind === "ADD" && !after) return false;
    return text.trim().length > 0;
  };

  /* Handlers */
  const handleKindChange = (value: string) => {
    setKind(value as ChangeKind);
    setHeading("");
    setText("");
  };

  const handleSectionChange = (value: string) => {
    setHeading(value);
    const section = sections.find((s) => s.heading === value);
    setText(section ? section.text : "");
  };

  const handleSave = () => {
    if (kind === "ADD") {
      void onSave({ heading: heading.trim(), text, after });
      return;
    }
    void onSave({ heading, text: kind === "REMOVE" ? "" : text });
  };

  /* Render */
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex max-w-3xl flex-col gap-4">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-slate-700">
            {initialChange ? "Edit the change" : "Change Sadie's prompt"}
          </DialogTitle>
        </DialogHeader>
        <Select
          disabled={!!initialChange}
          onValueChange={handleKindChange}
          value={kind}
        >
          <SelectTrigger className={FIELD_TRIGGER_CLASS}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={FIELD_CONTENT_CLASS}>
            {Object.entries(KIND_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {kind === "ADD" ? (
          <Column className="gap-2">
            <Input
              className="h-9 text-sm"
              onChange={(e) => {
                setHeading(e.target.value);
              }}
              placeholder="Its heading, like BE BRIEF"
              value={heading}
            />
            <SectionSelect
              onChange={setAfter}
              placeholder="Goes after"
              sections={sections}
              value={after}
            />
          </Column>
        ) : (
          <SectionSelect
            onChange={handleSectionChange}
            placeholder="Pick a section"
            sections={sections}
            value={heading}
          />
        )}
        {kind === "REMOVE" ? (
          <Text className="text-xs font-normal text-slate-500">
            She runs without this section until you remove the change.
          </Text>
        ) : (
          <Textarea
            className="min-h-[320px] font-mono text-sm"
            onChange={(e) => {
              setText(e.target.value);
            }}
            placeholder="What the section says"
            value={text}
          />
        )}
        <DialogFooter>
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button disabled={!isReady() || isSaving} onClick={handleSave}>
            {isSaving ? "Saving" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SectionSelect = ({
  onChange,
  placeholder,
  sections,
  value,
}: {
  onChange: (heading: string) => void;
  placeholder: string;
  sections: SadieSimulatorPromptSection[];
  value: string;
}) => (
  <Select onValueChange={onChange} value={value}>
    <SelectTrigger className={FIELD_TRIGGER_CLASS}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className={FIELD_CONTENT_CLASS}>
      {sections.map((section) => (
        <SelectItem key={section.heading} value={section.heading}>
          {section.heading}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);
